import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  MessageCircle, Send, X, Users, Circle, Loader2, Search,
  ChevronLeft, Phone, Video, MoreVertical, Smile, Bell, BellOff, Settings
} from "lucide-react";

const getToken = () => {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
};

export function ChatWidget({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Push notifications
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    testNotification
  } = usePushNotifications();

  // Handle notification click from service worker
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        setIsOpen(true);
        // If conversation ID is in data, select that conversation
        if (event.data.data?.conversation_id) {
          const conv = conversations.find(c => c.conversation_id === event.data.data.conversation_id);
          if (conv) {
            loadMessages(conv);
          }
        }
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [conversations]);

  // Connect WebSocket
  useEffect(() => {
    if (!user) return;
    
    const token = getToken();
    if (!token) return;
    
    const wsUrl = API.replace("https://", "wss://").replace("http://", "ws://");
    
    const connectWebSocket = () => {
      wsRef.current = new WebSocket(`${wsUrl}/chat/ws/${token}`);
      
      wsRef.current.onopen = () => {
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      };
      
      wsRef.current.onclose = (event) => {
        // Reconnect after 3 seconds if not intentional close
        if (event.code !== 1000) {
          setTimeout(connectWebSocket, 3000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error("Chat WebSocket error:", error);
      };
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [user]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case "online_users":
        setOnlineUsers(data.users || []);
        break;
      
      case "presence":
        setOnlineUsers(prev => {
          if (data.is_online) {
            const exists = prev.find(u => u.user_id === data.user_id);
            if (!exists) {
              return [...prev, { user_id: data.user_id, name: data.user_name }];
            }
          } else {
            return prev.filter(u => u.user_id !== data.user_id);
          }
          return prev;
        });
        // Update contacts list
        setContacts(prev => prev.map(c => 
          c.user_id === data.user_id ? { ...c, is_online: data.is_online } : c
        ));
        break;
      
      case "chat_message":
        // Add message to current conversation if it matches
        if (selectedConversation?.conversation_id === data.conversation_id) {
          setMessages(prev => [...prev, {
            message_id: data.message_id,
            sender_id: data.sender_id,
            sender_name: data.sender_name,
            content: data.content,
            created_at: data.created_at
          }]);
          // Send read receipt
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: "read_receipt",
              conversation_id: data.conversation_id
            }));
          }
        } else {
          // Increment unread count
          setUnreadCount(prev => prev + 1);
          // Show notification
          toast.info(`New message from ${data.sender_name}`, {
            action: {
              label: "View",
              onClick: () => setIsOpen(true)
            }
          });
        }
        // Refresh conversations list
        fetchConversations();
        break;
      
      case "message_sent":
        // Confirmation that our message was sent
        break;
      
      case "typing":
        setIsTyping(data.sender_name);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 2000);
        break;
      
      default:
        break;
    }
  }, [selectedConversation]);

  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const res = await authFetch(`${API}/chat/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (_error) {
      console.error("Error fetching conversations:", _error);
    }
  };

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const res = await authFetch(`${API}/chat/contacts`);
      if (res.ok) {
        setContacts(await res.json());
      }
    } catch (_error) {
      console.error("Error fetching contacts:", _error);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const res = await authFetch(`${API}/chat/unread-count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (_error) {
      console.error("Error fetching unread count:", _error);
    }
  };

  // Load data when widget opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      fetchContacts();
    }
  }, [isOpen]);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load messages for selected conversation
  const loadMessages = async (conversation) => {
    setLoading(true);
    setSelectedConversation(conversation);
    try {
      const res = await authFetch(`${API}/chat/conversations/${conversation.conversation_id}/messages`);
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (_error) {
      console.error("Error loading messages:", _error);
    } finally {
      setLoading(false);
    }
  };

  // Start new conversation
  const startConversation = async (contact) => {
    try {
      const res = await authFetch(`${API}/chat/start-conversation`, {
        method: "POST",
        body: JSON.stringify({ user_id: contact.user_id })
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedConversation({
          conversation_id: data.conversation_id,
          other_user: data.other_user
        });
        setMessages([]);
        setShowContacts(false);
        fetchConversations();
      }
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "chat_message",
        recipient_id: selectedConversation.other_user?.user_id,
        content: newMessage.trim()
      }));
      
      // Optimistically add message
      setMessages(prev => [...prev, {
        message_id: `temp_${Date.now()}`,
        sender_id: user.user_id,
        sender_name: user.name,
        content: newMessage.trim(),
        created_at: new Date().toISOString()
      }]);
      
      setNewMessage("");
    }
  };

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        recipient_id: selectedConversation.other_user?.user_id
      }));
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return formatTime(dateStr);
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredContacts = contacts.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const otherUser = selectedConversation?.other_user;
  const isOtherUserOnline = otherUser && onlineUsers.some(u => u.user_id === otherUser.user_id);

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-105"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Chat Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[900px] h-[600px] p-0 gap-0 overflow-hidden">
          <div className="flex h-full">
            {/* Sidebar */}
            <div className={`w-80 border-r bg-neutral-50 flex flex-col ${selectedConversation && !showContacts ? 'hidden md:flex' : 'flex'}`}>
              {/* Header */}
              <div className="p-4 border-b bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-lg">Messages</h2>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowContacts(!showContacts)}
                    className="text-emerald-600"
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {showContacts ? "Chats" : "New"}
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-neutral-100 border-0"
                  />
                </div>
              </div>

              {/* Content */}
              <ScrollArea className="flex-1">
                {showContacts ? (
                  // Contacts List
                  <div className="p-2">
                    <p className="text-xs text-neutral-500 px-2 py-1 mb-1">Start a conversation</p>
                    {filteredContacts.length === 0 ? (
                      <p className="text-sm text-neutral-500 text-center py-4">No contacts found</p>
                    ) : (
                      filteredContacts.map(contact => (
                        <button
                          key={contact.user_id}
                          onClick={() => startConversation(contact)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white transition-colors text-left"
                        >
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <span className="text-emerald-700 font-semibold">
                                {contact.name?.charAt(0) || "?"}
                              </span>
                            </div>
                            {contact.is_online && (
                              <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{contact.name}</p>
                            <p className="text-xs text-neutral-500 truncate">{contact.email}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px]">
                            {contact.role === "consultant" ? "Consultant" : "Client"}
                          </Badge>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  // Conversations List
                  <div className="p-2">
                    {conversations.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageCircle className="w-12 h-12 text-neutral-300 mx-auto mb-2" />
                        <p className="text-sm text-neutral-500">No conversations yet</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowContacts(true)}
                          className="mt-2 text-emerald-600"
                        >
                          Start a conversation
                        </Button>
                      </div>
                    ) : (
                      conversations.map(conv => (
                        <button
                          key={conv.conversation_id}
                          onClick={() => loadMessages(conv)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                            selectedConversation?.conversation_id === conv.conversation_id
                              ? "bg-emerald-50"
                              : "hover:bg-white"
                          }`}
                        >
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <span className="text-emerald-700 font-semibold">
                                {conv.other_user?.name?.charAt(0) || "?"}
                              </span>
                            </div>
                            {conv.is_online && (
                              <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm truncate">{conv.other_user?.name}</p>
                              <span className="text-xs text-neutral-400">
                                {conv.last_message_at && formatDate(conv.last_message_at)}
                              </span>
                            </div>
                            <p className="text-xs text-neutral-500 truncate">
                              {conv.last_message || "No messages yet"}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-emerald-600 text-white">
                              {conv.unread_count}
                            </Badge>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Notification Settings */}
              <div className="p-3 border-t bg-white">
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-xs font-medium text-neutral-600 flex items-center gap-1">
                    <Settings className="w-3 h-3" />
                    Notifications
                  </span>
                  {pushSubscribed ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">On</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Off</Badge>
                  )}
                </button>
                {showSettings && (
                  <div className="mt-2 pt-2 border-t space-y-2">
                    {pushSupported ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-neutral-600">Push notifications</span>
                          <Switch
                            checked={pushSubscribed}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                subscribePush().then(success => {
                                  if (success) toast.success("Notifications enabled");
                                  else toast.error("Failed to enable notifications");
                                });
                              } else {
                                unsubscribePush();
                                toast.success("Notifications disabled");
                              }
                            }}
                            disabled={pushLoading}
                          />
                        </div>
                        {pushSubscribed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs h-7"
                            onClick={() => {
                              testNotification();
                              toast.success("Test notification sent");
                            }}
                          >
                            Send test notification
                          </Button>
                        )}
                        {pushPermission === 'denied' && (
                          <p className="text-[10px] text-red-500">
                            Notifications blocked. Enable in browser settings.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[10px] text-neutral-500">
                        Push notifications not supported in this browser
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Online Users */}
              {onlineUsers.length > 0 && (
                <div className="p-3 border-t bg-white">
                  <p className="text-xs text-neutral-500 mb-2">Online now ({onlineUsers.length})</p>
                  <div className="flex -space-x-2">
                    {onlineUsers.slice(0, 5).map(u => (
                      <div
                        key={u.user_id}
                        className="w-8 h-8 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center"
                        title={u.name}
                      >
                        <span className="text-xs font-semibold text-emerald-700">
                          {u.name?.charAt(0)}
                        </span>
                      </div>
                    ))}
                    {onlineUsers.length > 5 && (
                      <div className="w-8 h-8 rounded-full bg-neutral-100 border-2 border-white flex items-center justify-center">
                        <span className="text-xs text-neutral-600">+{onlineUsers.length - 5}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b bg-white flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedConversation(null)}
                      className="md:hidden"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold">
                          {otherUser?.name?.charAt(0) || "?"}
                        </span>
                      </div>
                      {isOtherUserOnline && (
                        <Circle className="absolute bottom-0 right-0 w-3 h-3 fill-green-500 text-green-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{otherUser?.name}</p>
                      <p className="text-xs text-neutral-500">
                        {isOtherUserOnline ? "Online" : "Offline"}
                        {isTyping && ` · ${isTyping} is typing...`}
                      </p>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4 bg-neutral-50">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <MessageCircle className="w-12 h-12 text-neutral-300 mb-2" />
                        <p className="text-neutral-500">No messages yet</p>
                        <p className="text-sm text-neutral-400">Send a message to start the conversation</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg, idx) => {
                          const isOwn = msg.sender_id === user?.user_id;
                          return (
                            <div
                              key={msg.message_id || idx}
                              className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                                  isOwn
                                    ? "bg-emerald-600 text-white rounded-br-md"
                                    : "bg-white text-neutral-900 rounded-bl-md shadow-sm"
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                                <p className={`text-[10px] mt-1 ${isOwn ? "text-emerald-100" : "text-neutral-400"}`}>
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="p-4 border-t bg-white">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                        className="flex-1"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                // No conversation selected
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-neutral-50">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <MessageCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Welcome to Chat</h3>
                  <p className="text-neutral-500 mb-4">
                    Select a conversation or start a new one
                  </p>
                  <Button
                    onClick={() => setShowContacts(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    New Conversation
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
